import React, { useState, useEffect, useMemo } from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Dialog, DialogTitle, Box, Typography, IconButton, TextField, Button,
    Chip, CircularProgress, Alert, InputAdornment,
} from '@mui/material';
import { Close, Search as SearchIcon } from '@mui/icons-material';
import { businessAPI } from '../api';
import { productCategoryList } from '../utils/groupByCategory';

// True when a product has different rates on different days (day-specific overrides).
const hasDayRates = (p) => !!(p && p.day_price_overrides && Object.keys(p.day_price_overrides).length > 0);

// Bulk-edit the base price of many products on one page. Each row is prefilled with the
// current price; only the rows the vendor actually changes are saved (one price-log entry
// each, effective today). This sets the flat "all days" price — day-specific prices are
// managed per-product in the price dialog.
const BulkPriceDialog = ({ open, onClose, businessId, businessProducts, onProductsUpdated }) => {
    const [values, setValues] = useState({});   // { [productId]: string }
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);   // null = All
    const [effMode, setEffMode] = useState(null);   // 'today' | 'tomorrow' | 'other' — no default; vendor must choose
    const [customDate, setCustomDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    // Editable products: skip archived, the auto-managed delivery-charge product, and
    // any product with different rates per day (those set a flat price here would be lost —
    // edit them from the product's own ₹ Price screen instead).
    const products = useMemo(
        () => (businessProducts || [])
            .filter(p => !p.f_is_archived && !p.f_is_delivery_product && !hasDayRates(p))
            .sort((a, b) => (a.f_sort_order ?? 0) - (b.f_sort_order ?? 0) || (a.f_name || '').localeCompare(b.f_name || '')),
        [businessProducts]
    );

    // How many editable-but-day-priced products we're hiding, for an explanatory note.
    const dayRateCount = useMemo(
        () => (businessProducts || []).filter(p => !p.f_is_archived && !p.f_is_delivery_product && hasDayRates(p)).length,
        [businessProducts]
    );

    useEffect(() => {
        if (!open) return;
        setValues({});   // start blank — the old rate is shown alongside; type only what changes
        setSearch(''); setError(''); setResult(null);
        setEffMode(null); setCustomDate(''); setCategoryFilter(null);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const isoToday = () => new Date().toISOString().slice(0, 10);
    const isoTomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
    // Resolved effective date, or '' until the vendor has fully chosen one.
    const effectiveFrom = effMode === 'today' ? isoToday()
        : effMode === 'tomorrow' ? isoTomorrow()
        : effMode === 'other' ? customDate
        : '';

    const isChanged = (p) => {
        const v = (values[p.f_id] ?? '').trim();
        if (v === '') return false;
        const n = parseFloat(v);
        if (isNaN(n) || n <= 0) return false;
        return n !== parseFloat(p.current_price ?? NaN);
    };

    const changedItems = products.filter(isChanged);

    // Distinct categories across the editable products (for the filter chips).
    const categories = useMemo(
        () => [...new Set(products.flatMap(p => productCategoryList(p)))].sort(),
        [products]
    );

    const filtered = useMemo(() => {
        let list = products;
        if (categoryFilter) list = list.filter(p => productCategoryList(p).includes(categoryFilter));
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(p => p.f_name?.toLowerCase().includes(q));
        }
        return list;
    }, [products, search, categoryFilter]);

    const handleSave = async () => {
        if (!changedItems.length || !effectiveFrom) return;
        setSaving(true);
        setError('');
        setResult(null);
        try {
            const items = changedItems.map(p => ({
                f_business_product_id: p.f_id,
                base_price: parseFloat(values[p.f_id]),
            }));
            const res = await businessAPI.bulkUpdatePrices(businessId, {
                items,
                f_reason: 'Bulk price update',
                f_effective_from: effectiveFrom,
            });
            setResult({ updated: res.data?.updated ?? items.length });
            setValues({});   // reset inputs; the refreshed "old" column now shows the new rates
            if (onProductsUpdated) await onProductsUpdated();
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (typeof d === 'string' ? d : 'Failed to update prices'));
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setSearch(''); setError(''); setResult(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} disableScrollLock={false} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Update Prices</Typography>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}><Close /></IconButton>
                </Box>
            </DialogTitle>

            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* Sticky search + effective date + hint */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <TextField
                        size="small" fullWidth placeholder="Search products…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {categories.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                            <Chip label="All" size="small" clickable
                                color={!categoryFilter ? 'primary' : 'default'}
                                variant={!categoryFilter ? 'filled' : 'outlined'}
                                onClick={() => setCategoryFilter(null)} />
                            {categories.map(c => (
                                <Chip key={c} label={c} size="small" clickable
                                    color={categoryFilter === c ? 'primary' : 'default'}
                                    variant={categoryFilter === c ? 'filled' : 'outlined'}
                                    onClick={() => setCategoryFilter(categoryFilter === c ? null : c)} />
                            ))}
                        </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                        New prices apply from *
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                        {[{ v: 'today', l: 'Today' }, { v: 'tomorrow', l: 'Tomorrow' }, { v: 'other', l: 'Other date' }].map(o => (
                            <Chip key={o.v} label={o.l} size="small" clickable
                                color={effMode === o.v ? 'primary' : 'default'}
                                variant={effMode === o.v ? 'filled' : 'outlined'}
                                onClick={() => setEffMode(o.v)} />
                        ))}
                        {effMode === 'other' && (
                            <TextField
                                size="small" type="date" value={customDate} autoFocus
                                onChange={e => setCustomDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 160 }}
                            />
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        Only edited products are saved.
                        {dayRateCount > 0 && ` ${dayRateCount} product${dayRateCount !== 1 ? 's' : ''} with day-wise rates ${dayRateCount !== 1 ? 'are' : 'is'} hidden — edit ${dayRateCount !== 1 ? 'those' : 'it'} from the product's ₹ Price screen.`}
                    </Typography>
                </Box>

                {/* Column headers — line up over the Old / New values below */}
                {filtered.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.6,
                        bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ width: 56, textAlign: 'right', flexShrink: 0 }}>Old</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 104, textAlign: 'center', flexShrink: 0 }}>New</Typography>
                    </Box>
                )}

                {/* Scrollable list */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography variant="body2" color="text.secondary">No products to show.</Typography>
                        </Box>
                    ) : filtered.map(p => {
                        const changed = isChanged(p);
                        const isMonthly = (p.f_billing_type || p.product_billing_type) === 'MonthlyFlatFee';
                        const oldPrice = p.current_price != null ? parseFloat(p.current_price) : null;
                        return (
                            <Box key={p.f_id}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
                                    bgcolor: changed ? 'primary.50' : 'background.paper',
                                }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={500} noWrap>{p.f_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {isMonthly ? 'Monthly' : 'Per item'}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ width: 56, textAlign: 'right', flexShrink: 0, color: 'text.secondary' }}>
                                    {oldPrice != null ? `₹${oldPrice}` : '—'}
                                </Typography>
                                <TextField
                                    size="small" type="number"
                                    placeholder={oldPrice != null ? String(oldPrice) : '0'}
                                    value={values[p.f_id] ?? ''}
                                    onFocus={() => {
                                        // Prefill with the current price on first tap so the vendor edits from it.
                                        if ((values[p.f_id] ?? '') === '' && oldPrice != null) {
                                            setValues(v => ({ ...v, [p.f_id]: String(oldPrice) }));
                                        }
                                    }}
                                    onChange={e => setValues(v => ({ ...v, [p.f_id]: e.target.value }))}
                                    inputProps={{ min: 0, step: 0.01, inputMode: 'decimal', pattern: '[0-9]*', style: { textAlign: 'right' } }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    sx={{ width: 104, flexShrink: 0, bgcolor: 'background.paper' }}
                                />
                            </Box>
                        );
                    })}
                </Box>

                {/* Footer */}
                <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
                    {result && (
                        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setResult(null)}>
                            {result.updated} price{result.updated !== 1 ? 's' : ''} updated.
                        </Alert>
                    )}
                    {changedItems.length > 0 && !effectiveFrom && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.75 }}>
                            Choose when the new prices start (above).
                        </Typography>
                    )}
                    <Button
                        variant="contained" fullWidth disableElevation
                        disabled={changedItems.length === 0 || saving || !effectiveFrom}
                        onClick={handleSave}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none' }}
                    >
                        {saving ? 'Saving…' : (changedItems.length ? `Save ${changedItems.length} price${changedItems.length !== 1 ? 's' : ''}` : 'No changes')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default BulkPriceDialog;
